import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, BadgeCheck, Leaf, Clock, Star } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, IMAGES } from "../lib/data";

const LINES = ["Des vitres", "impeccables,", "sans effort."];

const BADGES = [
  { icon: BadgeCheck, label: "Satisfait ou refait" },
  { icon: Leaf, label: "Eau pure écologique" },
  { icon: Clock, label: "Intervention en 48h" },
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const yImage = useTransform(scrollY, [0, 700], [0, 110]);
  const yCard = useTransform(scrollY, [0, 700], [0, 60]);

  return (
    <section id="accueil" data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-sky-100 blur-3xl opacity-70 pointer-events-none" />
      <div className="absolute top-64 -left-40 w-[380px] h-[380px] rounded-full bg-emerald-50 blur-3xl opacity-80 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-sky-700"
            data-testid="hero-overline"
          >
            Nettoyage de vitres — Lille & Métropole
          </motion.p>

          <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-[4.2rem] font-extrabold tracking-tight leading-[1.05] text-slate-900">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-sky-600" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
          >
            WM Clean redonne de l'éclat à vos fenêtres, vitrines et vérandas. Particuliers et
            professionnels, ponctuel ou régulier — un résultat garanti, sans traces.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.95 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#devis"
              data-testid="hero-cta-devis"
              className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold px-7 py-3.5 shadow-xl shadow-sky-600/30 transition-all hover:-translate-y-0.5"
            >
              Demander un devis gratuit <ArrowRight size={17} />
            </a>
            <a
              href={PHONE_TEL}
              data-testid="hero-cta-phone"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white hover:border-sky-300 hover:text-sky-700 text-slate-700 font-semibold px-6 py-3.5 transition-colors"
            >
              <Phone size={17} className="text-sky-600" /> {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {BADGES.map((b) => (
              <span
                key={b.label}
                data-testid={`hero-badge-${b.label.toLowerCase().replace(/\s/g, "-")}`}
                className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm"
              >
                <b.icon size={14} className="text-emerald-500" /> {b.label}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-sky-900/20 border-4 border-white">
            <motion.img
              src={IMAGES.hero}
              alt="Nettoyage professionnel de vitres à Lille"
              data-testid="hero-image"
              style={{ y: yImage, scale: 1.15 }}
              className="w-full h-[420px] lg:h-[540px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-sky-900/30 via-transparent to-transparent" />
          </div>

          <motion.div
            style={{ y: yCard }}
            className="absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl bg-white/90 backdrop-blur-md border border-sky-100 shadow-xl px-5 py-4 flex items-center gap-3"
            data-testid="hero-rating-card"
          >
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
              ))}
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 leading-tight">4,9/5</p>
              <p className="text-xs text-slate-500">+200 clients dans la métropole</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
